import {
    Close as CloseIcon,
    DragIndicator as DragIcon,
    AccountTree as PipelineIcon,
    Settings as SettingsIcon,
    Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
    useTheme,
} from '@mui/material';
import React, { memo, useState } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';

import { useCanvasStore } from '../stores/canvasStore.js';
import { CanvasMode, Widget, WidgetConfig, WidgetState } from '../types/index.js';
import { getTypeColor } from '../utils/typeColors.js';
import { widgetRegistry } from '../utils/widgetRegistry.js';
// import { useCanvasMode } from '../hooks/useCanvasMode.js';

interface WidgetNodeProps extends NodeProps {
  data: Widget;
}

export const WidgetNode = memo<WidgetNodeProps>(({ data: widget, selected }) => {
  const theme = useTheme();
  // const canvasMode = useCanvasMode(); // TODO: Use for responsive widget rendering
  const removeWidget = useCanvasStore((state) => state.removeWidget);
  const updateWidgetConfig = useCanvasStore((state) => state.updateWidgetConfig);
  const updateWidgetOutputs = useCanvasStore((state) => state.updateWidgetOutputs);
  const updateWidgetInputs = useCanvasStore((state) => state.updateWidgetInputs);
  const updateWidgetState = useCanvasStore((state) => state.updateWidgetState);
  const activeCanvas = useCanvasStore((state) => state.getActiveCanvas());
  const createPipelineGroup = useCanvasStore((state) => state.createPipelineGroup);
  const expandGroup = useCanvasStore((state) => state.expandGroup);
  // Only allow edits on custom canvases; market and profile are read-only
  const canEdit = activeCanvas?.kind === 'custom';
  const showOutputHandles = canEdit;
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  
  // Get widget definition from registry
  const widgetDef = widgetRegistry.get(widget.type);
  const WidgetComponent = widgetDef?.component;

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit) return;
    if (activeCanvas) {
      removeWidget(activeCanvas.id, widget.id);
    }
  };

  const handleSettings = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit) return;
    // TODO: Open widget settings dialog
    console.log('Opening settings for widget:', widget.id);
  };

  // Widget prop handlers
  const handleUpdateConfig = React.useCallback((config: Partial<WidgetConfig>) => {
    if (activeCanvas) {
      updateWidgetConfig(activeCanvas.id, widget.id, config);
    }
  }, [activeCanvas?.id, widget.id, updateWidgetConfig]);

  const handleUpdateOutputs = React.useCallback((outputs: Partial<Record<string, any>>) => {
    if (activeCanvas) {
      updateWidgetOutputs(activeCanvas.id, widget.id, outputs);
    }
  }, [activeCanvas?.id, widget.id, updateWidgetOutputs]);

  const handleUpdateState = React.useCallback((state: Partial<WidgetState>) => {
    if (activeCanvas) {
      updateWidgetState(activeCanvas.id, widget.id, state);
    }
  }, [activeCanvas?.id, widget.id, updateWidgetState]);

  const handleUpdateInputs = React.useCallback((inputs: Partial<Record<string, any>>) => {
    if (activeCanvas) {
      updateWidgetInputs(activeCanvas.id, widget.id, inputs);
    }
  }, [activeCanvas?.id, widget.id, updateWidgetInputs]);
  
  // Handle context menu
  const handleContextMenu = (e: React.MouseEvent) => {
    if (!canEdit) return;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };
  
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };
  
  const handleCreatePipelineGroup = () => {
    if (!canEdit) return;
    if (activeCanvas) {
      const name = prompt('Enter a name for this pipeline:');
      if (name) {
        createPipelineGroup(activeCanvas.id, widget.id, name);
      }
    }
    handleCloseContextMenu();
  };
  
  const handleUngroup = () => {
    if (!canEdit) return;
    if (activeCanvas && widget.type === 'widgetGroup') {
      const groupConfig = widget.config as any;
      if (groupConfig.groupId) {
        expandGroup(activeCanvas.id, groupConfig.groupId, widget.id);
      }
    }
    handleCloseContextMenu();
  };
  

  return (
    <>
      {/* Input handles */}
      {widget.inputs.map((input, index) => {
        // Find the input definition to get the schema
        // For widget groups, use the schema from the input itself
        const inputDef = widgetDef?.inputDefinitions?.find(def => def.id === input.id);
        const handleColor = getTypeColor(inputDef?.schema || input.schema);
        
        return (
          <Handle
            key={input.id}
            type="target"
            position={Position.Left}
            id={input.id}
            style={{
              top: `${((index + 1) * 100) / (widget.inputs.length + 1)}%`,
              background: handleColor,
              width: 12,
              height: 12,
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: theme.shadows[2],
            }}
          >
          <Box
            sx={{
              position: 'absolute',
              right: 15,
              top: '50%',
              transform: 'translateY(-50%)',
              background: theme.palette.background.paper,
              padding: '2px 6px',
              borderRadius: 1,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              opacity: selected ? 1 : 0,
              transition: 'opacity 0.2s',
              pointerEvents: 'none',
              boxShadow: theme.shadows[1],
            }}
          >
            {input.name}
          </Box>
        </Handle>
        );
      })}

      {/* Widget Card */}
      <Card
        onContextMenu={canEdit ? handleContextMenu : undefined}
        sx={{
          width: widget.size.width,
          // Auto-height for rich text / feed-style widgets
          height: (['impactAnalysis','runHeader','widgetGroup'].includes(widget.type)) ? 'auto' : widget.size.height,
          minHeight: widget.type === 'impactAnalysis' ? 400 : (widgetDef?.defaultSize.height || 200),
          maxHeight: (['impactAnalysis','runHeader','widgetGroup'].includes(widget.type)) ? 'none' : 800,
          cursor: canEdit ? 'move' : 'default',
          boxShadow: selected 
            ? '0 0 0 1px rgba(116,6,242,0.7), 0 12px 40px -16px rgba(116,6,242,0.45)'
            : theme.shadows[2],
          border: '1px solid rgba(255,255,255,0.08)',
          transition: 'box-shadow 0.2s, border-color 0.2s, height 0.3s ease',
          '&:hover': {
            boxShadow: selected
              ? '0 0 0 1px rgba(116,6,242,0.75), 0 16px 48px -16px rgba(116,6,242,0.55)'
              : theme.shadows[4],
          },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardHeader
          sx={{
            padding: 1.25,
            '& .MuiCardHeader-action': {
              margin: 0,
            },
            // Subtle top accent line for visual identity
            position: 'relative',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, rgba(116,6,242,0.7), rgba(0,122,255,0.6))',
              opacity: 0.4,
            },
          }}
          avatar={
            <DragIcon
              sx={{
                color: 'text.secondary',
                fontSize: 20,
                cursor: 'grab',
                '&:active': {
                  cursor: 'grabbing',
                },
              }}
            />
          }
          title={
            <Typography variant="subtitle2" noWrap>
              {widget.config.title || widget.type}
            </Typography>
          }
          action={
            canEdit ? (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" onClick={handleSettings} className="nodrag nopan">
                  <SettingsIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleRemove} className="nodrag nopan">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : null
          }
        />
        <CardContent
          sx={{
            padding: 0,
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
          className="nopan nodrag"
        >
          {WidgetComponent ? (
            <WidgetComponent
              widget={widget}
              onUpdateConfig={handleUpdateConfig}
              onUpdateOutputs={handleUpdateOutputs}
              onUpdateState={handleUpdateState}
              onUpdateInputs={handleUpdateInputs}
              isSelected={selected}
              canvasMode={CanvasMode.DESKTOP}
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography color="error" variant="body2">
                Widget type "{widget.type}" not found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Output handles (hidden on read-only canvases) */}
      {showOutputHandles && widget.outputs.map((output, index) => {
        // Find the output definition to get the schema
        // For widget groups, use the schema from the output itself
        const outputDef = widgetDef?.outputDefinitions?.find(def => def.id === output.id);
        const handleColor = getTypeColor(outputDef?.schema || output.schema);
        
        return (
          <Handle
            key={output.id}
            type="source"
            position={Position.Right}
            id={output.id}
            style={{
              top: `${((index + 1) * 100) / (widget.outputs.length + 1)}%`,
              background: handleColor,
              width: 12,
              height: 12,
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: theme.shadows[2],
            }}
          >
          <Box
            sx={{
              position: 'absolute',
              left: 15,
              top: '50%',
              transform: 'translateY(-50%)',
              background: theme.palette.background.paper,
              padding: '2px 6px',
              borderRadius: 1,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              opacity: selected ? 1 : 0,
              transition: 'opacity 0.2s',
              pointerEvents: 'none',
              boxShadow: theme.shadows[1],
            }}
          >
            {output.name}
          </Box>
        </Handle>
        );
      })}
      
      {/* Context Menu */}
      <Menu
        open={canEdit && contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          canEdit && contextMenu !== null
            ? { top: contextMenu.y, left: contextMenu.x }
            : undefined
        }
      >
        {widget.type === 'widgetGroup' && canEdit && (
          <MenuItem onClick={handleUngroup}>
            <ListItemIcon>
              <UnarchiveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ungroup</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleCreatePipelineGroup} disabled={!canEdit}>
          <ListItemIcon>
            <PipelineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Group Pipeline to Here</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSettings} disabled={!canEdit}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRemove} disabled={!canEdit}>
          <ListItemIcon>
            <CloseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
});
